// YIN fundamental-frequency detection (pure TS, the spec's documented fallback path, E-9/E-11).
// Reference: de Cheveigné & Kawahara (2002). Robust for harmonic-rich violin signal.

export interface YinResult {
  freq: number; // Hz, 0 if none
  confidence: number; // 0..1
}

const DEFAULT_THRESHOLD = 0.15;

export function yinDetect(buffer: Float32Array, sampleRate: number, threshold = DEFAULT_THRESHOLD): YinResult {
  const minFreq = 65;
  const maxFreq = 2000;
  const tauMin = Math.max(2, Math.floor(sampleRate / maxFreq));
  const tauMax = Math.min(Math.floor(buffer.length / 2), Math.floor(sampleRate / minFreq));

  const diff = new Float32Array(tauMax);
  // Difference function.
  for (let tau = tauMin; tau < tauMax; tau++) {
    let sum = 0;
    for (let i = 0; i < tauMax; i++) {
      const delta = buffer[i] - buffer[i + tau];
      sum += delta * delta;
    }
    diff[tau] = sum;
  }

  // Cumulative mean normalized difference.
  const cmnd = new Float32Array(tauMax);
  cmnd[0] = 1;
  let running = 0;
  for (let tau = 1; tau < tauMax; tau++) {
    running += diff[tau];
    cmnd[tau] = running === 0 ? 1 : (diff[tau] * tau) / running;
  }

  // Absolute threshold: first dip below threshold, then walk to local minimum.
  let tauEstimate = -1;
  for (let tau = tauMin; tau < tauMax; tau++) {
    if (cmnd[tau] < threshold) {
      while (tau + 1 < tauMax && cmnd[tau + 1] < cmnd[tau]) tau++;
      tauEstimate = tau;
      break;
    }
  }

  if (tauEstimate === -1) {
    // No value below threshold: take global minimum as a low-confidence fallback.
    let minVal = Infinity;
    let minTau = -1;
    for (let tau = tauMin; tau < tauMax; tau++) {
      if (cmnd[tau] < minVal) {
        minVal = cmnd[tau];
        minTau = tau;
      }
    }
    if (minTau === -1) return { freq: 0, confidence: 0 };
    tauEstimate = minTau;
  }

  // Parabolic interpolation for sub-sample precision.
  const betterTau: number =
    tauEstimate > 0 && tauEstimate + 1 < tauMax
      ? parabolicInterpolation(cmnd, tauEstimate)
      : tauEstimate;

  const freq = sampleRate / betterTau;
  const confidence = Math.max(0, Math.min(1, 1 - cmnd[tauEstimate]));
  return { freq: freq > 0 ? freq : 0, confidence };
}

function parabolicInterpolation(cmnd: Float32Array, tau: number): number {
  const s0 = cmnd[tau - 1];
  const s1 = cmnd[tau];
  const s2 = cmnd[tau + 1];
  const denom = s0 + s2 - 2 * s1;
  if (denom === 0) return tau;
  const shift = (s0 - s2) / (2 * denom);
  return tau + shift;
}
